/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from '../../../components/Containers/ArgosFlexbox';
import ArgosMenuItem from '../../../components/Form/Select/ArgosMenuItem';
import ArgosSelect from '../../../components/Form/Select/ArgosSelect';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import React, {useEffect, useState} from 'react';
import {renderModelNameString, renderVersionString} from '../../../components/Text/TextFormatting';
import toast from 'react-hot-toast';
import {usePostRequest} from '../../../../api/helper';
import {ArgosSelectOption} from '../../../components/Form/Select/types';
import {SCENARIO_ANALYSIS_CONSTANTS} from '../constants';
import {ModelData} from '../../../../helpers/models';
import {ArgosFavoriteIconLogic} from '../../../components/Buttons/ArgosFavoriteIconLogic';
import {ETrackedItemTypes} from '../../../../constants/trackedItems';


/* Endpoints */
const fetchModelsEndpoint = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.modelsList
const fetchVersionsEndpoint = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.versionsList
const fetchVariationsEndpoint = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.variationsList

/* Error messages */
const fetchModelsError = "An error occurred while fetching the Models list"
const fetchVersionsError = "An error occurred while fetching the Versions list"
const fetchVariationsError = "An error occurred while fetching the Variations list"


/**
 * SelectModel component props
 */
interface SelectModelProps {
  modelData: ModelData
  trackedItemId: string
  handleChange: any
  disableSelect: boolean
}


/**
 * Fetch menu options response type
 */
interface FetchOptionsResponse {
  values: ArgosSelectOption[]
}

/**
 * Renders the Model selection dropdowns on the View Model page
 * TODO: Commonize this component with the one in the Coverage Requirements app
 */
const SelectModel = (props: SelectModelProps) => {
  const {modelData, trackedItemId, handleChange, disableSelect} = props

  const [selectedModel, setSelectedModel] = useState<string>(modelData.model ?? '')
  const [selectedVersion, setSelectedVersion] = useState<string>(modelData.version ?? '')
  const [selectedVariation, setSelectedVariation] = useState<string>(modelData.variation ?? '')

  const [models, setModels] = useState<ArgosSelectOption[]>([])
  const [versions, setVersions] = useState<ArgosSelectOption[]>([])
  const [variations, setVariations] = useState<ArgosSelectOption[]>([])

  const [isLoadingModels, setIsLoadingModels] = useState<boolean>(false)
  const [isLoadingVersions, setIsLoadingVersions] = useState<boolean>(false)
  const [isLoadingVariations, setIsLoadingVariations] = useState<boolean>(false)

  /**
   * Fetch models list
   */
  const fetchModelsHandle = usePostRequest(fetchModelsEndpoint)
  const fetchModels = () => {
    setIsLoadingModels(true)
    fetchModelsHandle().then((resp: FetchOptionsResponse) => {
      setModels(resp.values)
    }).catch((e) => {
      console.log(e)
      toast.error(fetchModelsError)
    }).finally(() => {
      setIsLoadingModels(false)
    })
  }

  /**
   * Fetch versions list
   */
  const fetchVersionsHandle = usePostRequest(fetchVersionsEndpoint)
  const fetchVersions = (model: string) => {
    setIsLoadingVersions(true)
    const req = {
      'model': model
    }
    fetchVersionsHandle(req).then((resp: FetchOptionsResponse) => {
      setVersions(resp.values)
    }).catch((e) => {
      toast.error(fetchVersionsError)
      console.log(e)
    }).finally(() => {
      setIsLoadingVersions(false)
    })
  }

  /**
   * Fetch variations list
   */
  const fetchVariationsHandle = usePostRequest(fetchVariationsEndpoint)
  const fetchVariations = (model: string, version: string) => {
    setIsLoadingVariations(true)
    const req = {
      'model': model,
      'version': version
    }
    fetchVariationsHandle(req).then((resp: FetchOptionsResponse) => {
      setVariations(resp.values)
    }).catch((e) => {
      toast.error(fetchVariationsError)
      console.log(e)
    }).finally(() => {
      setIsLoadingVariations(false)
    })
  }

  /**
   * Update selected model, version, and variation on model data change
   */
  useEffect(() => {
    setSelectedModel(modelData.model ?? '')
    setSelectedVersion(modelData.version ?? '')
    setSelectedVariation(modelData.variation ?? '')
  }, [modelData])

  /**
   * Fetch models list on component mount
   */
  useEffect(() => {
    fetchModels()
  }, [])

  /**
   * Fetch versions list on model change
   */
  useEffect(() => {
    if (selectedModel !== '') {
      fetchVersions(selectedModel)
    }
  }, [selectedModel])

  /**
   * Fetch variations list on version change
   */
  useEffect(() => {
    if (selectedModel !== '' && selectedVersion !== '') {
      fetchVariations(selectedModel, selectedVersion)
    }
  }, [selectedVersion])

  return (
    <ArgosFlexbox style={{flex: 1, flexDirection: 'row', alignItems: 'center', padding: '10px'}}>
      {models.length > 0 && <FormControl variant='standard'>
        <InputLabel id='model-label'>Model</InputLabel>
        <ArgosSelect
          id='model'
          labelId='model-label'
          type='text'
          value={selectedModel}
          label='Model'
          onChange={(e) => handleChange('model', e.target.value)}
          disabled={isLoadingModels || disableSelect}
        >
          {models.map((option: ArgosSelectOption) =>
            <ArgosMenuItem key={option.id} value={option.value}>
              {renderModelNameString(option.label)}
            </ArgosMenuItem>
          )}
        </ArgosSelect>
      </FormControl>}

      {versions.length > 0 && <FormControl variant='standard'>
        <InputLabel id='version-label'>Version</InputLabel>
        <ArgosSelect
          id='version'
          labelId='version-label'
          type='text'
          value={selectedVersion}
          label='Version'
          onChange={(e) => handleChange('version', e.target.value)}
          disabled={selectedModel === '' || isLoadingVersions || disableSelect}
        >
          {versions.map((option: ArgosSelectOption) => 
            <ArgosMenuItem key={option.id} value={option.value}>
              {renderVersionString(option.label)}
            </ArgosMenuItem>
          )}
        </ArgosSelect>
      </FormControl>}

      {variations.length > 0 && <FormControl variant='standard' style={{flex: 1}}>
        <InputLabel id='variation-label'>Variation</InputLabel>
        <ArgosSelect
          id='variation'
          labelId='variation-label'
          type='text'
          value={selectedVariation}
          label='Variation'
          onChange={(e) => handleChange('variation', e.target.value)}
          disabled={selectedVersion === '' || isLoadingVariations || disableSelect}
        >
          {variations.map((option: ArgosSelectOption) =>
            <ArgosMenuItem key={option.id} value={option.value}>
              {option.label}
            </ArgosMenuItem>
          )}
        </ArgosSelect>
      </FormControl>}

      {trackedItemId !== "" && <ArgosFavoriteIconLogic 
        favoriteType={ETrackedItemTypes.SCENARIO_ANALYSIS} 
        trackedItemUid={trackedItemId} 
        partitionKey={trackedItemId}
      />}

    </ArgosFlexbox>
  )
}

export default SelectModel
